import { events } from "@frend-digital/events/store";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { useCallback, useEffect, useMemo, type MouseEventHandler } from "react";
import type { BaseProduct } from "../../server";
import { useAddToCart, useSelection } from "./hooks";

type Item = Pick<
  NonNullable<BaseProduct["items"]>[number],
  "item" | "name" | "sku" | "sizeId"
> & {
  stock?:
    | ("preorder" | "infinite" | "yes" | "no" | "no" | "few")
    | number
    | string
    | boolean;
};

type StateTypes =
  | "select_size"
  | "out_of_stock"
  | "add_to_cart"
  | "coming_soon";

type BuyBoxState = {
  type: StateTypes;
  label: string;
  fewLeft?: boolean;
  disabled: boolean;
  preorder?: boolean;
  nameAsSizeId?: boolean;
};

let previousEventId: string | undefined;

/**
 * Buy box hook. MUST BE USED within a Suspense boundary
 */
export const useBuyBox = ({
  selectedSize,
  labels,
  sizes,
  onSuccess,
  onNotify,
  comingSoon,
  query: { key = "size", type = "name" } = {},
}: {
  selectedSize?: string | undefined;
  sizes: Item[] | undefined;
  labels: Record<StateTypes, string> & { preorder?: string };
  onNotify?: (type: StateTypes, item: Item) => void;
  onSuccess?: () => void;
  comingSoon?: boolean;
  query?: {
    key?: string;
    type?: "name" | "sizeId";
  };
}) => {
  const searchParams = useUrlSearchParams({
    shallow: true,
    replace: true,
  });

  const size = !!selectedSize ? selectedSize : searchParams.get(key);

  const addToCart = useAddToCart({
    onSuccess,
  });

  const selectedItem = useMemo(
    () =>
      sizes?.find((item) => {
        if (type === "sizeId") {
          return item.sizeId === size;
        }
        return item.name === size;
      }),
    [sizes, size, type]
  );

  const { data } = useSelection({
    select: (data) => data.selection,
  });

  useEffect(() => {
    if (!selectedItem) return;
    if (previousEventId === selectedItem.item) return;

    const itemId = selectedItem.item;

    events.dispatch("select_product_size", data, {
      dispatcher: "centra",
      item_id: itemId,
      depictProductId: itemId?.split("-")?.[1],
    });

    previousEventId = itemId;
  }, [selectedItem, data]);

  function stateReducer(item?: Item): BuyBoxState {
    if (!item) {
      return {
        type: "select_size",
        label: labels.select_size,
        disabled: true,
      };
    }

    switch (true) {
      case comingSoon:
        return {
          type: "coming_soon",
          label: labels.coming_soon,
          disabled: false,
        };
      case typeof item.stock === "number" && item.stock === 0:
        return {
          type: "out_of_stock",
          label: labels.out_of_stock,
          disabled: false,
        };
      case item.stock === "no":
        return {
          type: "out_of_stock",
          label: labels.out_of_stock,
          disabled: false,
        };
      case !item.stock:
        return {
          type: "out_of_stock",
          label: labels.out_of_stock,
          disabled: false,
        };

      default:
        return {
          type: "add_to_cart",
          label: labels.add_to_cart,
          fewLeft: item.stock === "few",
          disabled: false,
          preorder: item.stock === "preorder",
        };
    }
  }

  const state = useMemo(() => stateReducer(selectedItem), [selectedItem]);

  const handleClick = useCallback(() => {
    if (state.type === "select_size") return;

    if (!selectedItem?.item) return;

    if (state.type === "out_of_stock")
      return onNotify?.(state.type, selectedItem);
    if (state.type === "coming_soon")
      return onNotify?.(state.type, selectedItem);

    addToCart.mutate({
      itemId: selectedItem.item!,
    });
  }, [state, selectedItem]);

  const getButtonProps = useCallback(
    (props?: {}) => {
      const mergedClickHandler: MouseEventHandler = (e) => {
        if (props && "onClick" in props && typeof props.onClick === "function")
          props.onClick(e);
        handleClick();
      };

      return {
        ...props,
        "data-name": "buy-box-button",
        "data-state": addToCart.isPending ? "pending" : "idle",
        "data-type": state.type,
        disabled: state.disabled,
        onClick: mergedClickHandler,
      };
    },
    [state]
  );

  const getButtonLabel = useCallback(() => {
    return state.label;
  }, [state]);

  const selectSize = useCallback(
    (item: Item) => {
      const value = type === "sizeId" ? item.sizeId : item.name;
      if (!value) {
        console.warn("Invalid value for size", item);
        return;
      }
      searchParams.set(key, value!);
    },
    [searchParams, type]
  );

  const getSizeProps = useCallback(
    (item: Item, props: {} = {}) => {
      if (!item.sizeId) return props;
      const mergedClickHandler: MouseEventHandler = (e) => {
        if ((e.target as HTMLElement).nodeName === "A") {
          // prevent defaults
          e.preventDefault();
        }
        if (props && "onClick" in props && typeof props.onClick === "function")
          props.onClick(e);
        selectSize(item);
      };

      return {
        ...props,
        "data-size": item.sizeId,
        "data-selected":
          selectedItem?.sizeId === item.sizeId ? true : undefined,
        "data-name": "buy-box-size",
        onClick: mergedClickHandler,
      };
    },
    [selectedItem]
  );

  return {
    state,
    item: selectedItem,
    sizes: sizes,
    getButtonProps,
    getButtonLabel,
    getSizeProps,
    selectSize,
    pending: addToCart.isPending,
    mutation: addToCart,
  };
};
